import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { csvDataApi } from "../../RTKQuery/CsvDataService/csvDataApi";
import Loader from "../Loader/Loader";
import {
  useGetCampaignUsersQuery,
  useGetGroupedCampaignUsersQuery,
  useLazyExportCampaignUsersQuery,
  useLazyExportGroupedCampaignUsersQuery,
} from "../../RTKQuery/CsvDataService/csvDataApi";
import {
  useGetSingleCampaignQuery,
  useGetGroupedCampaignsQuery,
} from "../../RTKQuery/CampaignService/campaignApi";
import DataTable from "../UIKit/CollapsibleTable";
import PageComponentHeader from "../PageComponentHeader";
import ExportModal from "../Modals/ExportModal/ExportModal";
import UploadCampaignStats from "../UIKit/Buttons/UploadCampaignStats";
import FilterDrawer from "../UIKit/Drawer";
import ConditionalFilters from "../ConditionalFilters";
import { CSVLink } from "react-csv";
import { generateCampaignCSV } from "../../utils/csv";
import { campaignDetailsColumns } from "../../constants/tableColumns";
import {
  Box,
  Typography,
  Divider,
  Button,
  Paper,
  CircularProgress,
  Chip,
} from "@mui/material";
import CampaignStatusChip from "../UIKit/Chip/CampaignStatusChip";
import {
  containerBox,
  overviewPaper,
  headerSection,
  titleSection,
  titleTypography,
  sourceChip,
  infoSection,
  subjectText,
  senderEmailText,
  senderEmailsContainer,
  senderEmailsLabel,
  senderEmailsList,
  senderEmailChip,
  divider,
  statsGrid,
  statCard,
  statLabelTypography,
  statValueTypography,
  rateStatCard,
  usersTableHeader,
  usersTableTitle,
  filteredResultsTypography,
  actionsBox,
  exportButton,
  loaderBox,
  emptyStatePaper,
  emptyStateTypography,
  campaignLoaderBox,
  notFoundBox,
  notFoundTitleTypography,
  notFoundBodyTypography,
  hiddenCsvLink,
  stepSubjectsContainer,
  stepSubjectsLabel,
  stepSubjectItem,
  stepChip,
  stepSubjectText,
  statusSection,
  statusChip,
  actionButton,
} from "./styles";
import {
  sourceColors,
  defaultSourceColors,
  sourceChipBase,
} from "../CampaignsList/styles";

const CampaignDetailsComponent = () => {
  const CAMPAIGN_DETAILS_USERS_COLUMN_ORDER_STORAGE_KEY =
    "campaign-details-users-column-order";
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isGroupedView = location.pathname.includes("/grouped");
  const searchParams = new URLSearchParams(location.search);
  const campaignIdsParam = searchParams.get("ids");
  const campaignIds = campaignIdsParam ? campaignIdsParam.split(",") : [];

  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [loader, setLoader] = useState(true);
  const [filterLoader, setFilterLoader] = useState(false);
  const [conditionCount, setConditionCount] = useState(0);
  const [subConditionCount, setSubConditionCount] = useState(0);
  const [saveFilterSelection, setSaveFilterSelection] = useState(false);
  const [filter, setFilter] = React.useState("");
  const [originalFilters, setOriginalFilters] = useState([]);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const defaultFilter = [
    {
      id: "f-1",
      first: true,
      conditions: [
        {
          id: "f-10",
          first: true,
          subConditions: [],
          filter: "f-1",
        },
      ],
    },
  ];
  const [filterData, setFilterData] = useState(defaultFilter);
  const [closeFilterMenu, setCloseFilterMenu] = useState(true);
  const downloadRef = useRef();

  const { data: singleCampaignData, isLoading: fetchingSingle } =
    useGetSingleCampaignQuery(id, { skip: isGroupedView });

  const { data: groupedCampaignData, isLoading: fetchingGrouped } =
    useGetGroupedCampaignsQuery(campaignIds, {
      skip: !isGroupedView || campaignIds.length === 0,
    });

  const campaignData = isGroupedView ? groupedCampaignData : singleCampaignData;
  const fetching = isGroupedView ? fetchingGrouped : fetchingSingle;
  const { data: singleUsersData, isLoading: isLoadingSingle } =
    useGetCampaignUsersQuery(
      {
        id,
        page: pagination.pageIndex,
        rows: pagination.pageSize,
        filters: JSON.stringify(filters),
      },
      {
        skip: isGroupedView,
        pollingInterval: 10000,
      }
    );

  const { data: groupedUsersData, isLoading: isLoadingGrouped } =
    useGetGroupedCampaignUsersQuery(
      {
        campaignIds: campaignIds,
        page: pagination.pageIndex,
        rows: pagination.pageSize,
        filters: filters,
      },
      {
        skip: !isGroupedView || campaignIds.length === 0,
        pollingInterval: 10000,
      }
    );

  const data = isGroupedView ? groupedUsersData : singleUsersData;
  const isLoading = isGroupedView ? isLoadingGrouped : isLoadingSingle;

  const [triggerExportSingle, { isLoading: exportLoadingSingle }] =
    useLazyExportCampaignUsersQuery();
  const [triggerExportGrouped, { isLoading: exportLoadingGrouped }] =
    useLazyExportGroupedCampaignUsersQuery();

  const exportLoading = isGroupedView
    ? exportLoadingGrouped
    : exportLoadingSingle;

  const handleApplyFilters = () => {
    setFilterLoader(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setFilters(filterData);
    setCloseFilterMenu(true);
  };

  const handleResetFilters = () => {
    setFilterLoader(true);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setFilterData([
      {
        id: "f-1",
        first: true,
        conditions: [
          {
            id: "f-10",
            first: true,
            subConditions: [],
            filter: "f-1",
          },
        ],
      },
    ]);
    setFilters([]);
    setFilter("");
    setSaveFilterSelection(false);
    setOriginalFilters([]);
    setCloseFilterMenu(true);
  };

  const handleExport = async (exportType) => {
    try {
      if (isGroupedView) {
        if (campaignIds.length > 0) {
          await triggerExportGrouped({
            campaignIds,
            filters,
            exportType,
          }).unwrap();
        }
      } else {
        await triggerExportSingle({ id, filters, exportType }).unwrap();
      }
      setExportModalOpen(false);
    } catch (error) {
      console.error("Error exporting users:", error);
    }
  };

  const handleOpenExportModal = () => {
    setExportModalOpen(true);
  };

  const handleCloseExportModal = () => {
    setExportModalOpen(false);
  };

  useEffect(() => {
    if (data) {
      setUsers(data.users);
      setEmail(data?.users[0]?.campaigns[0]?.campaignBody || "");
      setLoader(false);
      setFilterLoader(false);
    }
  }, [data]);

  useEffect(() => {
    if (filterLoader) {
      const timeout = setTimeout(() => {
        setFilterLoader(false);
      }, 10000);

      return () => clearTimeout(timeout);
    }
  }, [filterLoader]);

  const csvData = generateCampaignCSV(users);

  const hasCampaignData = campaignData && !fetching;

  const hasUserData = users && users.length > 0;

  const hasActiveFilters = () => {
    if (!filters || filters.length === 0) return false;

    return filters.some((filter) => {
      if (!filter.conditions || filter.conditions.length === 0) return false;

      return filter.conditions.some((condition) => {
        const hasDirectValues =
          condition.category &&
          condition.subOperator &&
          condition.value !== undefined &&
          condition.value !== "";

        const hasSubConditionValues =
          condition.subConditions &&
          condition.subConditions.length > 0 &&
          condition.subConditions.some((subCondition) => {
            return (
              subCondition.category &&
              subCondition.subOperator &&
              subCondition.value !== undefined &&
              subCondition.value !== ""
            );
          });

        return hasDirectValues || hasSubConditionValues;
      });
    });
  };

  const isFilterActive = hasActiveFilters();

  const defaultCampaignDetailsColumnOrder = useMemo(() => {
    return campaignDetailsColumns
      .map((column) => column.id || column.accessorKey)
      .filter(Boolean);
  }, []);

  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const storedValue = localStorage.getItem(
        CAMPAIGN_DETAILS_USERS_COLUMN_ORDER_STORAGE_KEY
      );
      const parsedValue = storedValue ? JSON.parse(storedValue) : [];
      return Array.isArray(parsedValue) ? parsedValue : [];
    } catch (error) {
      return [];
    }
  });

  useEffect(() => {
    if (!defaultCampaignDetailsColumnOrder.length) return;

    setColumnOrder((previousOrder) => {
      const safePreviousOrder = Array.isArray(previousOrder)
        ? previousOrder.filter((columnId) =>
            defaultCampaignDetailsColumnOrder.includes(columnId)
          )
        : [];
      const missingColumns = defaultCampaignDetailsColumnOrder.filter(
        (columnId) => !safePreviousOrder.includes(columnId)
      );
      const nextOrder = [...safePreviousOrder, ...missingColumns];

      const isSameOrder =
        nextOrder.length === previousOrder.length &&
        nextOrder.every((columnId, index) => columnId === previousOrder[index]);

      return isSameOrder ? previousOrder : nextOrder;
    });
  }, [defaultCampaignDetailsColumnOrder]);

  useEffect(() => {
    if (!columnOrder.length) return;
    localStorage.setItem(
      CAMPAIGN_DETAILS_USERS_COLUMN_ORDER_STORAGE_KEY,
      JSON.stringify(columnOrder)
    );
  }, [columnOrder]);

  return (
    <>
      <PageComponentHeader allowBack></PageComponentHeader>

      {hasCampaignData && (
        <Box sx={containerBox}>
          <Paper sx={overviewPaper} elevation={0}>
            <Box sx={headerSection}>
              <Box sx={titleSection}>
                <Typography variant="h5" sx={titleTypography}>
                  {isGroupedView
                    ? `Grouped Campaigns (${campaignIds.length} campaigns)`
                    : campaignData?.campaignTitle || "Campaign Title"}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    flexWrap: "wrap",
                  }}
                >
                  {!isGroupedView && campaignData?.campaignSource && (
                    <Chip
                      label={campaignData.campaignSource}
                      size="small"
                      sx={{
                        ...sourceChipBase,
                        ...(sourceColors[campaignData.campaignSource] ||
                          defaultSourceColors),
                      }}
                    />
                  )}
                  {!isGroupedView &&
                    campaignData?.sentFromEmails &&
                    Array.isArray(campaignData.sentFromEmails) &&
                    campaignData.sentFromEmails.length > 0 && (
                      <Box sx={senderEmailsContainer}>
                        <Typography variant="body2" sx={senderEmailsLabel}>
                          Sent From:
                        </Typography>
                        <Box sx={senderEmailsList}>
                          {campaignData.sentFromEmails.map((email, index) => (
                            <Chip
                              key={index}
                              label={email}
                              size="small"
                              sx={senderEmailChip}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                </Box>
              </Box>
              {!isGroupedView && (
                <Box sx={statusSection}>
                  {campaignData?.status && (
                    <CampaignStatusChip
                      status={campaignData.status}
                      size="medium"
                    />
                  )}
                  {campaignData?.status === "draft" && (
                    <Button
                      variant="contained"
                      size="small"
                      sx={actionButton}
                      onClick={() => {
                        // Invalidate users cache before navigating
                        dispatch(csvDataApi.util.invalidateTags(["users"]));
                        navigate(`/prospects?campaignId=${id}`);
                      }}
                    >
                      Generate list
                    </Button>
                  )}
                  {(campaignData?.status === "Filters in progress" ||
                    campaignData?.status === "Suppression applied") && (
                      <Button
                        variant="contained"
                        size="small"
                        sx={actionButton}
                        onClick={() => {
                          // Invalidate users cache before navigating
                          dispatch(csvDataApi.util.invalidateTags(["users"]));
                          navigate(`/prospects?campaignId=${id}`);
                        }}
                      >
                        Continue list generation
                      </Button>
                    )}
                </Box>
              )}
            </Box>

            {!isGroupedView && (
              <Box sx={infoSection}>
                {campaignData?.campaignSource === "Apollo" &&
                  campaignData?.stepSubjects ? (
                  <Box sx={stepSubjectsContainer}>
                    <Typography variant="body2" sx={stepSubjectsLabel}>
                      Campaign Subjects by Step
                    </Typography>
                    {Object.entries(campaignData.stepSubjects)
                      .sort(([a], [b]) => parseInt(a) - parseInt(b))
                      .map(([step, subject]) => (
                        <Box key={step} sx={stepSubjectItem}>
                          <Chip
                            label={`Step ${step}`}
                            size="small"
                            sx={stepChip}
                          />
                          <Typography variant="body2" sx={stepSubjectText}>
                            {subject}
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={subjectText}>
                    {campaignData?.campaignSubject || "No subject available"}
                  </Typography>
                )}
              </Box>
            )}

            <Divider sx={divider} />

            <Box sx={statsGrid}>
              {!isGroupedView &&
                campaignData?.campaignSource === "Apollo" &&
                campaignData?.followUpNumber && (
                  <Box sx={statCard}>
                    <Typography sx={statLabelTypography}>
                      Campaign Total Steps
                    </Typography>
                    <Typography sx={statValueTypography}>
                      {campaignData.followUpNumber}
                    </Typography>
                  </Box>
                )}
                <Box sx={statCard}>
                <Typography sx={statLabelTypography}>Total Processed</Typography>
                <Typography sx={statValueTypography}>
                  {campaignData?.totalProcessed || 0}
                </Typography>
              </Box>
              <Box sx={statCard}>
                <Typography sx={statLabelTypography}>
                  Total Delivered
                </Typography>
                <Typography sx={statValueTypography}>
                  {campaignData?.totalSent || 0}
                </Typography>
              </Box>
              <Box sx={statCard}>
                <Typography sx={statLabelTypography}>Total Opens</Typography>
                <Typography sx={statValueTypography}>
                  {campaignData?.totalOpen || 0}
                </Typography>
              </Box>
              <Box sx={statCard}>
                <Typography sx={statLabelTypography}>Total Clicks</Typography>
                <Typography sx={statValueTypography}>
                  {campaignData?.clicks || 0}
                </Typography>
              </Box>
              <Box sx={statCard}>
                <Typography sx={statLabelTypography}>Total Replies</Typography>
                <Typography sx={statValueTypography}>
                  {campaignData?.replies || 0}
                </Typography>
              </Box>
              <Box sx={statCard}>
                <Typography sx={statLabelTypography}>Total Bounced</Typography>
                <Typography sx={statValueTypography}>
                  {campaignData?.bounced || 0}
                </Typography>
              </Box>
              <Box sx={statCard}>
                <Typography sx={statLabelTypography}>
                  Total Unsubscribed
                </Typography>
                <Typography sx={statValueTypography}>
                  {campaignData?.unsubScribed || 0}
                </Typography>
              </Box>
              <Box sx={statCard}>
                <Typography sx={statLabelTypography}>Total Dropped</Typography>
                <Typography sx={statValueTypography}>
                  {campaignData?.dropped || 0}
                </Typography>
              </Box>  
              <Box sx={rateStatCard}>
                <Typography sx={statLabelTypography}>Open Rate</Typography>
                <Typography sx={statValueTypography}>
                  {campaignData?.openRate?.toFixed(2) || "0.00"}%
                </Typography>
              </Box>
              <Box sx={rateStatCard}>
                <Typography sx={statLabelTypography}>Click Rate</Typography>
                <Typography sx={statValueTypography}>
                  {campaignData?.clickRate?.toFixed(2) || "0.00"}%
                </Typography>
              </Box>
              <Box sx={rateStatCard}>
                <Typography sx={statLabelTypography}>Reply Rate</Typography>
                <Typography sx={statValueTypography}>
                  {campaignData?.repliedRate?.toFixed(2) || "0.00"}%
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Box sx={usersTableHeader}>
            <Typography variant="h5" sx={usersTableTitle}>
              {isGroupedView
                ? "Grouped Campaign Prospects"
                : "Campaign Prospects"}
              {isFilterActive && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={filteredResultsTypography}
                >
                  (Filtered Results)
                </Typography>
              )}
            </Typography>
            <Box sx={actionsBox}>
              {/* <Button
                variant="contained"
                onClick={handleOpenExportModal}
                disabled={exportLoading}
                startIcon={
                  exportLoading ? <CircularProgress size={16} /> : null
                }
                sx={exportButton}
              >
                {exportLoading ? "Exporting..." : "Export"}
              </Button> */}
              {!isGroupedView &&
                data?.users[0]?.campaigns[0]?.campaign === "lemlist" && (
                  <UploadCampaignStats id={id} />
                )}
              <FilterDrawer
                close={closeFilterMenu}
                setClose={setCloseFilterMenu}
                setCloseFilterMenu={setCloseFilterMenu}
                filters={filters}
              >
                <ConditionalFilters
                  campaignUsers
                  onReset={handleResetFilters}
                  onSubmit={handleApplyFilters}
                  conditionCount={conditionCount}
                  setConditionCount={setConditionCount}
                  subConditionCount={subConditionCount}
                  setSubConditionCount={setSubConditionCount}
                  filtersArray={filterData}
                  setFiltersArray={setFilterData}
                  setCloseFilterMenu={setCloseFilterMenu}
                  defaultFilter={defaultFilter}
                  saveFilterSelection={saveFilterSelection}
                  setSaveFilterSelection={setSaveFilterSelection}
                  filter={filter}
                  setFilter={setFilter}
                  originalFilters={originalFilters}
                  setOriginalFilters={setOriginalFilters}
                  filterType={"campaign"}
                />
              </FilterDrawer>
            </Box>
          </Box>

          {isLoading || filterLoader ? (
            <Box sx={loaderBox}>
              <Loader />
            </Box>
          ) : hasUserData ? (
            <DataTable
              columns={campaignDetailsColumns}
              data={users}
              totalData={data?.total || 0}
              pagination={pagination}
              setPagination={setPagination}
              sorting={sorting}
              setSorting={setSorting}
              setLoader={setLoader}
              columnOrder={columnOrder}
              setColumnOrder={setColumnOrder}
            />
          ) : (
            <Paper sx={emptyStatePaper} elevation={0}>
              <Typography variant="h6" sx={emptyStateTypography}>
                {isFilterActive
                  ? "No users match your current filters"
                  : "No users found for this campaign"}
              </Typography>
              {isFilterActive && (
                <Button variant="contained" onClick={handleResetFilters}>
                  Reset Filters
                </Button>
              )}
            </Paper>
          )}
        </Box>
      )}

      {fetching && (
        <Box sx={campaignLoaderBox}>
          <Loader />
        </Box>
      )}

      {!fetching && !hasCampaignData && (
        <Box sx={notFoundBox}>
          <Typography variant="h5" sx={notFoundTitleTypography}>
            Campaign not found
          </Typography>
          <Typography variant="body1" sx={notFoundBodyTypography}>
            The campaign you're looking for doesn't exist or has been removed.
          </Typography>
        </Box>
      )}

      <CSVLink
        headers={csvData?.headers || []}
        data={csvData?.data || ""}
        filename="Campaign"
        style={hiddenCsvLink}
        ref={downloadRef}
      />

      <ExportModal
        open={exportModalOpen}
        onClose={handleCloseExportModal}
        onExport={handleExport}
        isLoading={exportLoading}
      />
    </>
  );
};

export default CampaignDetailsComponent;
